import campaignService from '@/services/campaigns'
import codesService from '@/services/codes'
import quotaService from '@/services/quota'
import * as types from '../mutations'
import Vue from 'vue'

const state = {
  codes: [],
  filteredCodeCount: 0,
  scmFields: [],
  activeFilters: [],
  loaded: false,
  scmLoaded: false,
  totalBlacklistedCodesCount: 0,
  totalBlacklistedAssignedCodesCount: 0,
  totalBlacklistedUnassignedCodesCount: 0,
  creatingCode: false,
  hasRenameError: false,
  renameErrorMsg: [],
  productCode: null,
  isEditing: false,
  isGs1FormLocked: false,
  isUpgradingToGs1: false,
  quota: {
    limit: 0,
    used: 0,
    remaining: 0
  }
}

const getters = {
  usedCodesCount: (state) => state.quota.used,
  totalCodesCount: (state) => state.quota.limit,
  isGs1FormLocked: (state) => state.isGs1FormLocked,
  isUpgradingToGs1: (state) => state.isUpgradingToGs1,
  totalBlacklistedCodesCount: (state) => state.totalBlacklistedCodesCount,
  totalBlacklistedAssignedCodesCount: (state) => state.totalBlacklistedAssignedCodesCount,
  scmFields: (state) => state.scmFields,
  activeScmFields: (state) => {
    return state.scmFields.filter((f) => {
      return !f.is_archived
    })
  },
  scmFieldLoaded: (state) => state.scmLoaded,
  activeCodesFilters: (state) => state.activeFilters,
  filteredCodeCount: (state) => state.filteredCodeCount,
  codes: (state) => state.codes,
  isCreatingCode: (state) => state.creatingCode,
  hasCodeRenameError: (state) => state.hasRenameError,
  codeRenameErrorMsg: (state) => state.renameErrorMsg,
  selectedProductCode: (state) => state.productCode,
  isCodeEditing: (state) => state.isEditing
}

const actions = {

  // Load all quotas, users + codes
  async loadQuotas ({ commit }) {
    try {
      const res = await quotaService.loadQuotas()
      if (res) {
        res.forEach((quota) => {
          if (quota.resource === 'code') {
            commit(types.SET_CODE_QUOTA, quota)
          }
          if (quota.resource === 'users') {
            commit(types.SET_USERS_QUOTA, quota)
          }
        })
      }
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  async loadScmFields ({ commit }, campaign) {
    const res = await campaignService.loadScmFields(campaign)
    commit(types.SET_SCM_FIELDS, res)
    commit(types.SET_SCM_LOADED, true)
    return res
  },

  async loadBlacklistedCodeCount ({ commit }) {
    const res = await codesService.loadCodes({ activation_status: 2, no_product: 'False' })
    // commit(types.SET_TOTAL_BLACKLISTED_CODES_COUNT, res.count)
    commit(types.SET_TOTAL_BLACKLISTED_AND_ASSIGNED_CODES_COUNT, res.count)
  },

  async loadProductCodeCount ({ commit }, { product }) {
    const res = await codesService.loadCodes({ product: product })
    commit(types.SET_PRODUCT_CODE_AND_COUNT, { productId: product, count: res.count, code: res.count ? res.results[0] : null })
    return Promise.resolve(res.results[0])
  },

  addCodeFilter ({ commit, dispatch, getters }, filter) {
    // Check if filter exist first
    const activeFilters = getters.activeCodesFilters

    const match = activeFilters.find((f) => {
      return f.key === filter.key
    })

    if (match) {
      commit(types.UPDATE_CODE_ACTIVE_FILTER, filter)
    } else {
      commit(types.ADD_CODE_ACTIVE_FILTER, filter)
    }
    // dispatch('loadCodes')
  },

  async generateOnDemandCode ({ commit }, { product, tags }) {
    try {
      const res = await codesService.generateOnDemandCode({ product: product.id, tags })
      if (!res.activated_at) {
        res.activated_at = new Date()
      }
      if (!res.message) {
        res.message = res.extended_id
      }
      commit(types.SET_PRODUCT_CODE_AND_COUNT, { productId: product.id, count: 1, code: res })
      commit(types.INCREASE_USED_CODE_COUNT)
      return Promise.resolve(res)
    } catch (err) {
      // quota.code.exceeded
      console.log(err)
      return Promise.reject(err)
    }
  },

  // async loadTotalCodes () {
  //   const res = await codesService.loadCodes({})
  //   return res
  // },

  async loadAllCodes ({ getters }, { count, campaign, product }) {
    const promises = []
    let toLoad = 0

    while (toLoad < count && toLoad <= 2000) {
      promises.push(codesService.loadCodes({ filters: getters.activeCodesFilters, activation_status: 1, campaign, product, offset: toLoad, limit: 500 }))
      toLoad += 500
    }

    return Promise.all(promises).then((data) => {
      let codes = []
      data.forEach((res) => {
        codes = codes.concat(res.results)
      })
      return Promise.resolve(codes)
    }).catch((err) => {
      return Promise.reject(err)
    })
  },

  async loadCodes ({ commit, getters }, { campaign, product, offset, noFilter, cancelToken }) {
    commit(types.SET_CODES_LOADED, false)
    const res = await codesService.loadCodes({ filters: noFilter ? null : getters.activeCodesFilters, activation_status: 1, campaign, product, offset, cancelToken })
    commit(types.SET_CODES_LIST, res)
    commit(types.SET_CODES_LOADED, true)
    return res
  },

  // async updateSelectedPrefixes ({ commit }, { codes, prefix, reference }) {
  //   try {
  //     const extendedIds = codes.flatMap((code) => code.message)
  //     const params = {
  //       constraints: { extended_id: extendedIds },
  //       scm_data: { scantrust_url_prefix: prefix },
  //       reference: reference
  //     }

  //     const res = await codesService.scmUploadAsync(params)
  //     commit(types.SET_CODE_PREFIX, { extendedIds: extendedIds, prefix: prefix })
  //     return Promise.resolve(res)
  //   } catch (err) {
  //     return Promise.reject(err)
  //   }
  // },

  // async saveTagsValue ({ commit }, { code, tags }) {
  //   const tagsCopy = JSON.parse(JSON.stringify(tags))
  //   const tagArray = [{}]
  //   let batchIndex = 0
  //   while (Object.keys(tagsCopy).length) {
  //     if (Object.keys(tagArray[batchIndex]).length >= 10) {
  //       batchIndex += 1
  //       tagArray[batchIndex] = {}
  //     }
  //     tagArray[batchIndex][Object.keys(tagsCopy)[0]] = Object.entries(tagsCopy)[0][1]
  //     delete tagsCopy[Object.keys(tagsCopy)[0]]
  //   }

  //   const data = []
  //   for (const [index, array] of tagArray.entries()) {
  //     try {
  //       data[index] = await codesService.updateScmValues({ code, tags: array })
  //     } catch (err) {
  //       return Promise.reject(err)
  //     }
  //   }

  //   if (data[0]) {
  //     let scmData = {}
  //     data.forEach((res) => {
  //       if (res.code && res.code.scm_data) {
  //         scmData = { ...scmData, ...res.code.scm_data }
  //       }
  //     })

  //     commit(types.UPDATE_CODE_SCM_DATA, { extendedId: data[0].code.extended_id, tags: scmData })
  //     return Promise.resolve()
  //   }
  // },

  // async updateTag ({ commit }, { tag }) {
  //   try {
  //     const res = await codesService.updateScmField(tag)
  //     commit(types.UPDATE_SCM_FIELD, res)
  //     return Promise.resolve(res)
  //   } catch (err) {
  //     return Promise.reject(err)
  //   }
  // },

  // async createTag ({ commit }, { tag }) {
  //   try {
  //     const res = await codesService.createScmField(tag)
  //     commit(types.ADD_SCM_FIELD_TO_LIST, res)
  //     return Promise.resolve(res)
  //   } catch (err) {
  //     return Promise.reject(err)
  //   }
  // },

  // async bulkTransferAndUpdate ({ commit }, { codes, product, tags, status }) {
  //   try {
  //     const items = []
  //     codes.forEach((code) => {
  //       items.push({
  //         extended_id: code.message,
  //         product: product,
  //         ...tags,
  //         status: status
  //       })
  //     })
  //     const res = await codesService.scmUpload({ key: 'extended_id', items })
  //     return Promise.resolve(res)
  //   } catch (err) {
  //     console.log(err)
  //   }
  // },

  async transferCode ({ commit }, { code, product, tags }) {
    try {
      const items = [{
        extended_id: code.message,
        product: product,
        ...tags
      }]

      const res = await codesService.scmUpload({ key: 'extended_id', items: items })
      return Promise.resolve(res.codes_affected)
    } catch (err) {
      return Promise.reject(err)
    }
  },

  async updateTags ({ state, commit }, { code, tags, productId }) {
    try {
      if (!code.message) {
        code.message = code.extended_id
      }
      const params = {
        extended_id: code.message,
        scm_data: tags
      }

      const res = await codesService.updateCode(params)
      commit(types.UPDATE_CODE_TAGS, { id: code.id, tags: tags })
      if (productId) {
        const updatedCode = { ...code, scm_data: { ...code.scm_data, ...res.scm_data } }
        console.log('UPDATED CODE _>>', updatedCode)
        commit(types.SET_PRODUCT_CODE_AND_COUNT, { productId, count: 1, code: updatedCode })
      }
      return Promise.resolve(res)
    } catch (err) {
      return Promise.reject(err)
    }
  },

  async renameCode ({ state, commit }, { product, source, gs1Id, tags }) {
    try {
      commit(types.SET_RENAME_CODE_ERROR, false)

      // Hack to rename extended id to message since on demand endpoint has different name
      if (!source.message) {
        source.message = source.extended_id
      }
      const params = {
        source: source.message,
        target: gs1Id,
        id_format: 'gs1',
        scm_data: {
          ...tags
        }
      }
      const res = await codesService.renameCode(params)
      if (product?.id) {
        const updatedCode = { ...source, message: gs1Id, scm_data: { ...tags, ...res.scm_data } }
        console.log('UPDATED CODE _>', updatedCode)
        commit(types.SET_PRODUCT_CODE_AND_COUNT, { productId: product.id, count: 1, code: updatedCode })
        return Promise.resolve(updatedCode)
      }
    } catch (err) {
      commit(types.SET_RENAME_CODE_ERROR, true)
      if (err.response) {
        if (err.response.status === 409 && err.response.data) {
          const errorMessage = [{ key: err.response.data?.error_code, message: err.response.data?.error_message }]
          commit(types.SET_RENAME_CODE_ERROR_MESSAGE, { errorMessage })
          return Promise.reject(errorMessage)
        } else if (err.response.status === 400 && err.response.data) {
          const sourceMessage = err.response.data?.source?.toString()?.replace(/This field/g, 'Standard code')
          const targetMessage = err.response.data?.target?.toString()?.replace(/This field/g, 'GS1 identifier code')
          const errorMessage = [sourceMessage, targetMessage]

          commit(types.SET_RENAME_CODE_ERROR_MESSAGE, { errorMessage })
          return Promise.reject(errorMessage)
        }
      } else if (err.request) {
        console.log('Request error:', err.request)
      } else {
        console.log('Request setup error:', err.message)
      }
      return Promise.reject(err)
    }
  }

  /* for renaming standard codes to gs1 identifier */
  // async activateCodeTransferAndRename ({ state, commit, getters, dispatch }, { product, gs1Code, tags }) {
  //   try {
  //     if (state.isRenameError) commit(types.SET_RENAME_CODE_ERROR, false)

  //     let results = null
  //     const codeExits = state.productCode && state.productCode?.message
  //     const inactiveCode = !codeExits ? getters.inactiveCodes[0] : null

  //     const params = {
  //       source: codeExits ? state.productCode.message : inactiveCode.message,
  //       target: gs1Code,
  //       id_format: 'gs1',
  //       scm_data: {
  //         ...tags,
  //         ...(inactiveCode && {
  //           product: product.id || product.sku,
  //           activation_status: 1
  //         })
  //       }
  //     }

  //     results = await codesService.renameCode(params)
  //     let code = null

  //     if (results && Object.keys(results).length > 0) {
  //       // if code exists, we are renaming the old code to a new code
  //       if (codeExits) {
  //         const id = state.productCode.id
  //         const message = results?.params?.target
  //         commit(types.UPDATE_CODE_MESSAGE, { id, message })
  //         commit(types.UPDATE_CODE_TAGS, { id, tags })

  //         code = {
  //           ...state.productCode,
  //           message
  //         }
  //       } else {
  //         // creation of new codes
  //         // code is returned for setting isNew flag
  //         code = {
  //           ...inactiveCode,
  //           activation_status: 1,
  //           activated_at: new Date(),
  //           message: results?.params?.target,
  //           product,
  //           brand: {
  //             ...product.brand
  //           }
  //         }
  //         commit(types.REMOVE_CODE_FROM_INACTIVE_LIST, inactiveCode)
  //         commit(types.ADD_CODE_TO_LIST, code)
  //         commit(types.INCREASE_PRODUCT_CODE_COUNT, { product: product.id, code })
  //       }
  //     }
  //     return Promise.resolve(code)
  //   } catch (err) {
  //     commit(types.SET_RENAME_CODE_ERROR, true)

  //     if (err.response) {
  //       if (err.response.status === 409 && err.response.data) {
  //         const message = err.response.data?.error_message

  //         const errorMessage = [message]
  //         commit(types.SET_RENAME_CODE_ERROR_MESSAGE, { errorMessage })
  //       } else if (err.response.status === 400 && err.response.data) {
  //         /**
  //          * replace 'this field' with required error messages -> Standard code is invalid
  //          ***/
  //         const sourceMessage = err.response.data?.source?.toString()?.replace(/This field/g, 'Standard code')
  //         const targetMessage = err.response.data?.target?.toString()?.replace(/This field/g, 'GS1 identifier code')
  //         const errorMessage = [sourceMessage, targetMessage]

  //         commit(types.SET_RENAME_CODE_ERROR_MESSAGE, { errorMessage })
  //       }
  //     } else if (err.request) {
  //       console.log('Request error:', err.request)
  //     } else {
  //       console.log('Request setup error:', err.message)
  //     }

  //     return Promise.reject(err)
  //   }
  // }
}

const mutations = {

  [types.SET_CODE_QUOTA] (state, quota) {
    state.quota = quota
  },

  [types.INCREASE_USED_CODE_COUNT] (state) {
    state.quota.used++
    state.quota.remaining--
  },

  [types.SET_SCM_FIELDS] (state, fields) {
    state.scmFields = fields
  },

  [types.ADD_SCM_FIELD_TO_LIST] (state, field) {
    state.scmFields.unshift(field)
  },

  [types.UPDATE_SCM_FIELD] (state, field) {
    const index = state.scmFields.findIndex((f) => {
      return f.key === field.key
    })
    if (~index) {
      state.scmFields[index] = field
    }
  },

  [types.SET_SCM_LOADED] (state, status) {
    state.scmLoaded = status
  },

  [types.SET_CODES_LOADED] (state, status) {
    state.loaded = status
  },

  [types.CLEAR_ACTIVE_CODE_FILTERS] (state) {
    state.activeFilters = []
  },

  [types.ADD_CODE_ACTIVE_FILTER] (state, filter) {
    state.activeFilters.push(filter)
  },

  [types.SET_TOTAL_BLACKLISTED_AND_ASSIGNED_CODES_COUNT] (state, count) {
    state.totalBlacklistedAssignedCodesCount = count
  },

  [types.SET_TOTAL_BLACKLISTED_CODES_COUNT] (state, count) {
    state.totalBlacklistedCodesCount = count
  },

  [types.REMOVE_CODE_ACTIVE_FILTER] (state, filter) {
    const index = state.activeFilters.findIndex((f) => f.key === filter.key)
    if (~index) {
      state.activeFilters.splice(index, 1)
    }
  },

  [types.UPDATE_CODE_ACTIVE_FILTER] (state, filter) {
    const index = state.activeFilters.findIndex((f) => f.key === filter.key)
    if (~index) {
      state.activeFilters[index] = filter
    }
  },

  [types.UPDATE_CODE_SCM_DATA] (state, { extendedId, tags }) {
    const index = state.codes.findIndex((c) => c.message === extendedId)
    if (~index) {
      Vue.set(state.codes[index], 'scm_data', tags)
    }
  },

  [types.SET_CODE_PREFIX] (state, { extendedIds, prefix }) {
    state.codes.filter(code => extendedIds.includes(code.message)).forEach(code => {
      Vue.set(code.scm_data, 'scantrust_url_prefix', prefix)
    })
  },

  [types.SET_CODE_ACTIVE_FILTERS] (state, filters) {
    state.activeFilters = filters
  },

  [types.SET_CODES_LIST] (state, res) {
    state.codes = res.results
    state.filteredCodeCount = res.count
  },

  [types.REMOVE_CODE_FROM_INACTIVE_LIST] (state, code) {
    const index = state.inactiveCodes.findIndex((c) => c.message === code.message)
    if (~index) {
      state.inactiveCodes.splice(index, 1)
    }
  },

  [types.SET_INACTIVE_CODES_LIST] (state, codes) {
    state.inactiveCodes = codes
    state.inactiveCodesLoaded = true
  },

  [types.SET_IS_CREATING_CODE] (state, payload) {
    state.creatingCode = payload
  },

  [types.SET_RENAME_CODE_ERROR] (state, payload) {
    state.hasRenameError = payload
  },

  [types.SET_RENAME_CODE_ERROR_MESSAGE] (state, payload) {
    state.renameErrorMsg = payload.errorMessage
  },

  [types.UPDATE_CODE_TAGS] (state, { id, tags }) {
    const index = state.codes.findIndex((c) => c.id === id)
    if (~index) {
      const scmData = state.codes[index].scm_data
      state.codes[index].scm_data = {
        ...scmData,
        ...tags
      }
    }
  },

  [types.SET_SELECTED_PRODUCT_CODE] (state, payload) {
    state.productCode = payload
  },

  [types.SET_CODE_EDITING] (state, payload) {
    state.isEditing = payload
  },

  [types.SET_GS1_FORM_LOCK_STATUS] (state, status) {
    state.isGs1FormLocked = status
  },

  [types.SET_UPGRADING_TO_GS1_STATUS] (state, status) {
    state.isUpgradingToGs1 = status
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
